/**
 * useRelatedTasksPicker Hook
 *
 * Independent, server-backed task picker for the Related Tasks wizard step.
 * Owns its own filters (search, assignee, date range, location) and lazy
 * infinite-scroll pagination — fully decoupled from the global tasks list and
 * the sidebar's `taskFilters`.
 *
 * Pattern mirrors `useWorkspaceMembers` (local state + debounce + paginated
 * loadMore). Because `tasksService.getAll` does not accept an AbortSignal, a
 * monotonic stale-guard counter (as in `useTaskSearch`) discards out-of-order
 * responses instead of an AbortController.
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/hooks
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants/pagination';
import { logger } from '@/utils/logger';
import { tasksService } from '@/services/api/tasksService';
import { buildTaskFiltersParams } from '@/store/slices/taskFiltersSlice';
import { mapTaskListItemFromResponse } from '@/store/mappers/taskMapper';
import type { TaskListItem } from '@/types';

/** Sort applied to the picker list — newest first, independent of sidebar sort. */
const PICKER_SORT_BY = 'createdAt' as const;

interface UseRelatedTasksPickerParams {
  /** Picker is active (panel mounted/open). Fetch is gated on this flag. */
  enabled: boolean;
  /** Task being edited — excluded from the available list. */
  editingTaskId?: string | null;
  /** Already-selected task ids — hidden from the available list. */
  selectedTaskIds: string[];
}

interface UseRelatedTasksPickerReturn {
  tasks: TaskListItem[];

  // Filters
  nameSearch: string;
  setNameSearch: (value: string) => void;
  assigneeId: string | null;
  setAssigneeId: (value: string | null) => void;
  dateFrom: Date | null;
  setDateFrom: (value: Date | null) => void;
  dateTo: Date | null;
  setDateTo: (value: Date | null) => void;
  locationSearch: string;
  setLocationSearch: (value: string) => void;

  // Status
  isLoading: boolean;
  hasLoaded: boolean;
  hasNextPage: boolean;
  totalCount: number;
  error: string | null;

  // Actions
  loadMore: () => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export function useRelatedTasksPicker({
  enabled,
  editingTaskId,
  selectedTaskIds,
}: UseRelatedTasksPickerParams): UseRelatedTasksPickerReturn {
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [nameSearch, setNameSearch] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [locationSearch, setLocationSearch] = useState('');

  const { debouncedValue: debouncedNameSearch } = useDebounce(
    nameSearch,
    SEARCH_DEBOUNCE_DELAY
  );
  const { debouncedValue: debouncedLocationSearch } = useDebounce(
    locationSearch,
    SEARCH_DEBOUNCE_DELAY
  );

  // Monotonic guard: newer fetches win, stale responses are discarded.
  const fetchIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      const currentFetchId = ++fetchIdRef.current;
      setIsLoading(true);
      setError(null);

      try {
        const params = buildTaskFiltersParams({
          search: debouncedNameSearch,
          status: [],
          priority: [],
          assignedToId: assigneeId ? [assigneeId] : [],
          dateFrom,
          dateTo,
          location: debouncedLocationSearch,
          page: pageNum,
          pageSize: DEFAULT_PAGE_SIZE,
          sortBy: PICKER_SORT_BY,
          sortDescending: true,
        });
        // The builder does not set `page` (sidebar drives it separately) — set it here.
        params.page = pageNum;

        const response = await tasksService.getAll(params);

        // Discard if a newer fetch started while this one was in flight.
        if (currentFetchId !== fetchIdRef.current) return;

        const mapped = response.data.items.map(mapTaskListItemFromResponse);

        setTasks((previous) => (append ? [...previous, ...mapped] : mapped));
        setHasNextPage(response.data.hasNextPage);
        setTotalCount(response.data.totalCount);
        setPage(pageNum);
      } catch (fetchError: unknown) {
        if (currentFetchId !== fetchIdRef.current) return;
        logger.error('Failed to fetch related tasks', 'useRelatedTasksPicker', {
          error: fetchError,
        });
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Failed to fetch tasks'
        );
        if (!append) setTasks([]);
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    },
    [debouncedNameSearch, debouncedLocationSearch, assigneeId, dateFrom, dateTo]
  );

  // Reset + refetch page 1 whenever the picker is enabled or any filter changes.
  // Gated on `enabled` so the fetch is lazy (only when the panel is open).
  useEffect(() => {
    if (!enabled) return;
    fetchPage(DEFAULT_PAGE, false);
  }, [enabled, fetchPage]);

  // Clear results when the picker is disabled so a re-open starts fresh.
  useEffect(() => {
    if (enabled) return;
    fetchIdRef.current += 1;
    setTasks([]);
    setPage(DEFAULT_PAGE);
    setHasNextPage(false);
    setTotalCount(0);
    setHasLoaded(false);
    setError(null);
  }, [enabled]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isLoading) return;
    fetchPage(page + 1, true);
  }, [hasNextPage, isLoading, page, fetchPage]);

  const hasActiveFilters = Boolean(
    nameSearch || assigneeId || dateFrom || dateTo || locationSearch
  );

  const clearFilters = useCallback(() => {
    setNameSearch('');
    setAssigneeId(null);
    setDateFrom(null);
    setDateTo(null);
    setLocationSearch('');
  }, []);

  // Client-side hide of the edited task and already-selected tasks. This is a
  // display concern only (the server has no concept of the in-progress form),
  // so it stays out of the request params.
  const visibleTasks = useMemo(() => {
    const selectedSet = new Set(selectedTaskIds);
    return tasks.filter((task) => {
      if (editingTaskId && task.id === editingTaskId) return false;
      if (selectedSet.has(task.id)) return false;
      return true;
    });
  }, [tasks, editingTaskId, selectedTaskIds]);

  return {
    tasks: visibleTasks,

    nameSearch,
    setNameSearch,
    assigneeId,
    setAssigneeId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    locationSearch,
    setLocationSearch,

    isLoading,
    hasLoaded,
    hasNextPage,
    totalCount,
    error,

    loadMore,
    hasActiveFilters,
    clearFilters,
  };
}
